import { useState, useEffect, useCallback } from 'react';
import { Stack, ProgressIndicator } from '@fluentui/react';
import Loader from '../../components/Loader/Loader';
import { DismissRegular, ArrowDownloadRegular } from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import styles from './TrafficLightReport.module.css';
import ReportList from '../../components/ReportList';
import ReportToolBar from '../../components/ReportToolBar';
import { ProfilePhotoStack } from '../../components/ProfilePhotoStack/ProfilePhotoStack';

import { ChatMessage } from '../../api';
import React from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useIntl } from 'react-intl';
import { deleteTrafficLightReport, fetchTrafficLightReports } from '../../api/api';
import { useAuthErrorContext } from '../../components/AuthErrorProvider';
import { DeleteButton } from '../../components/DeleteButton/DeleteButton';
import { formatDateTime } from '../../utils/dateUtils';
import { StatusBadge } from '../../components/StatusBadge/StatusBadge';
import { OrderByOption } from './types';
import { DeleteDialog } from '../../components/DeleteDialog/DeleteDialog';

const TrafficLightReport = () => {
  const intl = useIntl();
  const [activeCitation, setActiveCitation] =
    useState<[content: string, id: string, title: string, filepath: string, url: string, metadata: string]>();
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false);
  const [answers, setAnswers] = useState<ChatMessage[]>([]);
  const [loadingAIDocuments, setLoadingAIDocuments] = useState(false); // State to handle AI document loading
  const [checkedItems, setCheckedItems] = useState<any[]>([]); // State to track checked items
  const [orderBy, setOrderBy] = useState({ property: 'status', desc: true }); // State to track order by selection
  const [trafficLightReport, setTrafficLightReport] = useState<any[]>([]); // State to store traffic light reports
  const [showAuthError, setShowAuthError] = React.useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { handleAuthError } = useAuthErrorContext();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingFiles, setIsDeletingFiles] = useState(false);

  const handleDownload = async () => {
    if (checkedItems.length === 0 && trafficLightReport.length === 0) return; // Early return if nothing to download

    const filesToDownload = checkedItems.length > 0 ? checkedItems : trafficLightReport;

    if (filesToDownload.length === 1) {
      const file = filesToDownload[0];
      const response = await fetch(file.fullpath);
      const data = await response.blob();
      saveAs(data, file.filename);
    } else {
      const zip = new JSZip();

      for (const file of filesToDownload) {
        const response = await fetch(file.fullpath);
        const data = await response.blob();
        zip.file(file.filename, data);
      }

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `trafficLightReports_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.zip`);
    }
  };

  // TODO: Strongly type this as the traffic light report type
  const orderByOptions: OrderByOption<any>[] = [
    { key: 'createddate', text: 'Created Date', order: 'desc' },
    { key: 'filename', text: 'Name', order: 'asc' },
    { key: 'status', text: 'Status', order: 'asc' },
  ];

  const fetchReports = useCallback(async () => {
    try {
      const response = await fetchTrafficLightReports(undefined, handleAuthError);
      setTrafficLightReport(response.data ?? []);
    } catch (error) {
      if (!showAuthError) {
        toast.error(
          intl.formatMessage(
            { id: 'trafficLightReport.error.fetch' },
            { error: error instanceof Error ? error.message : String(error) },
          ),
        );
      }
    }
  }, [handleAuthError, showAuthError, intl]);

  const confirmDelete = async () => {
    if (trafficLightReport.length === 0) return;
    
    setIsDeletingFiles(true);
    
    // Use checked items if any are selected, otherwise use all reports
    const itemsToDelete = (checkedItems.length > 0 ? checkedItems : trafficLightReport)
      .map(item => ({ blob_name: item.filename }));

    try {
      await deleteTrafficLightReport(itemsToDelete, handleAuthError);
      // After successful deletion, refresh the list
      await fetchReports();
      // Clear checked items
      setCheckedItems([]);
      toast.success(intl.formatMessage({ id: 'trafficLightReport.success.delete' }));
    } catch (error) {
      toast.error(
        intl.formatMessage(
          { id: 'trafficLightReport.error.delete' },
          { error: error instanceof Error ? error.message : String(error) },
        ),
      );
    } finally {
      setIsDeletingFiles(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleDelete = () => {
    if (isDeletingFiles) return; // Prevent opening dialog if deletion is in progress
    setIsDeleteDialogOpen(true);
  };

  // Initial load effect
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!isInitialLoading) return;
      try {
        setIsLoading(true);
        await fetchReports();
      } catch (error) {
        console.error('Failed to fetch reports:', error);
      } finally {
        setIsLoading(false);
        setIsInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [fetchReports, isInitialLoading]);

  // Polling effect
  useEffect(() => {
    let interval: NodeJS.Timeout;

    const pollReports = async () => {
      if (showAuthError || isLoading || isInitialLoading) return;
      setIsLoading(true);
      try {
        await fetchReports();
      } finally {
        setIsLoading(false);
      }
    };

    if (!showAuthError && !isInitialLoading) {
      interval = setInterval(pollReports, 30000); // Poll every 30 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [showAuthError, isInitialLoading, fetchReports]);

  useEffect(() => {
    setCheckedItems([]);
  }, [orderBy]);

  return (
    <div className={styles.container}>
      <Stack horizontal className={styles.chatRoot}>
        <Stack.Item grow={1} style={{ height: '100%' }}>
          <div
            style={{
              height: '100%',
              padding: '2rem',
              borderRadius: '1rem',
              background: 'white',
            }}
          >
            {loadingAIDocuments ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                }}
              >
                <Loader />
              </div>
            ) : (
              <>
                <ReportToolBar orderByOptions={orderByOptions} setOrderBy={setOrderBy}>
                  {
                    <>
                      <button
                        type="button"
                        onClick={handleDownload}
                        disabled={trafficLightReport.length === 0}
                        className={'toolbar-button--primary'}
                      >
                        <ArrowDownloadRegular style={{ marginTop: 'auto', marginBottom: 'auto' }} /> Download (
                        {checkedItems.length === 0 ? 'All' : checkedItems.length})
                      </button>

                      <DeleteButton
                        itemCount={checkedItems.length}
                        onClick={handleDelete}
                        totalCount={trafficLightReport.length}
                        showAllOption={true}
                      />
                    </>
                  }
                </ReportToolBar>
                {isLoading || isInitialLoading ? (
                  <div className={styles.progressContainer}>
                    <ProgressIndicator />
                  </div>
                ) : (
                  <div className={styles.progressContainer}>
                    <ProgressIndicator percentComplete={0} />
                  </div>
                )}
                <ReportList
                  orderBy={orderBy}
                  items={trafficLightReport}
                  showPlaceholderIcon={false}
                  onChecked={checkedItems => {
                    setCheckedItems(checkedItems);
                  }}
                  renderContent={item => (
                    <div className={styles.listItem}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <ProfilePhotoStack report={item} />
                      </div>
                      <div className={styles.itemContent}>
                        <div className={styles.itemHeader}>
                          <div className={styles.itemName}>{item.filename}</div>
                          <StatusBadge status={item.status} />
                        </div>
                        <div className={styles.itemDetails}>
                          <span className={styles.dotSeparator} />
                          {item.createddate && formatDateTime(item.createddate).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}
                />
              </>
            )}
          </div>
        </Stack.Item>
      </Stack>
      
      <DeleteDialog
        hidden={!isDeleteDialogOpen}
        onDismiss={() => !isDeletingFiles && setIsDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        itemCount={checkedItems.length || trafficLightReport.length}
        messageId="trafficLightReport.delete.confirmMessage"
        titleId="trafficLightReport.delete.confirmTitle"
        isDeletionInProgress={isDeletingFiles}
      />
      
      {answers.length > 0 && isCitationPanelOpen && activeCitation && (
        <Stack.Item className={`${styles.citationPanel} ${styles.mobileStyles}`}>
          <Stack
            horizontal
            className={styles.citationPanelHeaderContainer}
            horizontalAlign="space-between"
            verticalAlign="center"
          >
            <span className={styles.citationPanelHeader}>Citations</span>
            <DismissRegular className={styles.citationPanelDismiss} onClick={() => setIsCitationPanelOpen(false)} />
          </Stack>
          <h5 className={`${styles.citationPanelTitle} ${styles.mobileCitationPanelTitle}`}>{activeCitation[2]}</h5>
          <ReactMarkdown
            className={`${styles.citationPanelContent} ${styles.mobileCitationPanelContent}`}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {activeCitation[0]}
          </ReactMarkdown>
        </Stack.Item>
      )}
    </div>
  );
};

export default TrafficLightReport;
